// @ts-nocheck
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import useGetGameFeed from "../../../hooks/games/useGetGameFeed.hook";
import {useAuthStore} from "../../../store/useAuth.store";
import * as styles from "./_gameFeedCls";
import FeedCard from "./components/FeedCard";

const GameFeed = () => {
  const {t} = useTranslation();
  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const {games, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading} = useGetGameFeed();
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const [activeIndex, setActiveIndex] = useState(0);

  // Hide footer and prevent outer page scroll while the feed is mounted.
  useEffect(() => {
    const footer = document.querySelector("footer");
    const contentArea = document.querySelector(".ant-layout-content");

    const hideFloatBtn = () => {
      const btn = document.getElementById("float-wallet-btn");
      if (btn) btn.style.display = "none";
      return btn;
    };

    if (footer) footer.style.display = "none";
    hideFloatBtn();
    if (contentArea) {
      contentArea.style.overflow = "hidden";
      contentArea.style.paddingBottom = "0";
    }
    document.body.style.overflow = "hidden";

    // Observe DOM in case FloatButton mounts after this effect
    const observer = new MutationObserver(() => {
      hideFloatBtn();
    });
    observer.observe(document.body, {childList: true, subtree: true});

    return () => {
      observer.disconnect();
      if (footer) footer.style.display = "";
      const btn = document.getElementById("float-wallet-btn");
      if (btn) btn.style.display = "";
      if (contentArea) {
        contentArea.style.overflow = "";
        contentArea.style.paddingBottom = "";
      }
      document.body.style.overflow = "";
    };
  }, []);

  // Intersection Observer for infinite scroll (load more games).
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {rootMargin: "400px"}
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Intersection Observer for detecting which card is in viewport (active game).
  const cardRefs = useRef([]);
  const setCardRef = useCallback(
    index => el => {
      cardRefs.current[index] = el;
    },
    []
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.feedIndex);
            if (!Number.isNaN(idx)) {
              setActiveIndex(idx);
            }
          }
        }
      },
      {threshold: 0.6, root: containerRef.current}
    );

    for (const ref of cardRefs.current) {
      if (ref) observer.observe(ref);
    }
    return () => observer.disconnect();
  }, [games.length]);

  const handleRequestAuth = () => {
    handleShowAuthModal();
  };

  if (isLoading) {
    return (
      <div className={styles.gameFeedPageCls}>
        <div className={styles.gameFeedHeaderCls}>
          <h1 className={styles.gameFeedTitleCls}>{t("gameFeedTitle")}</h1>
        </div>
        <div
          className={styles.feedLoadingGameCls}
          style={{marginTop: "var(--space-9)"}}
        >
          <div className={styles.feedLoadingSpinnerCls} />
          <span className={styles.feedLoadingTextCls}>{t("loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.gameFeedPageCls}>
      <div className={styles.gameFeedHeaderCls}>
        <h1 className={styles.gameFeedTitleCls}>{t("gameFeedTitle")}</h1>
      </div>

      <div
        className={styles.gameFeedContainerCls}
        ref={containerRef}
      >
        {games.map((game, index) => (
          <div
            key={`${game.game_provider}-${game.game_id}`}
            ref={setCardRef(index)}
            data-feed-index={index}
            className="w-full h-full flex-shrink-0 [scroll-snap-align:start] [scroll-snap-stop:always]"
          >
            <FeedCard
              game={game}
              isActive={index === activeIndex}
              onRequestAuth={handleRequestAuth}
            />
          </div>
        ))}

        {/* Sentinel for infinite scroll */}
        <div
          ref={sentinelRef}
          className={styles.feedSentinelCls}
        >
          {isFetchingNextPage && <div className={styles.feedLoadingSpinnerCls} />}
        </div>

        {!hasNextPage && games.length > 0 && <div className={styles.feedEndMessageCls}>{t("gameFeedEndOfFeed")}</div>}
      </div>
    </div>
  );
};

export default GameFeed;
